// Shared physics-tuner UI used by both the standalone /admin/physics
// page and the in-game side dock that pops out during a virtual
// game. Mutations write to the same singleton store (virtual3d-tuning),
// so changes apply on the next render frame whether the user is
// looking at the canvas or the dedicated page.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ChevronRight, Grid3x3, RotateCcw, Sliders, X } from "lucide-react";
import { useGridOverlay } from "@/lib/admin-grid";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DEFAULT_TUNING,
  resetTuning,
  setGhostBias,
  setTuning,
  usePhysicsTuning,
  type GhostBias,
  type PhysicsTuning,
} from "@/lib/virtual3d-tuning";
import {
  useUpdatePhysicsTuning,
  type PhysicsTuningBlob,
} from "@/lib/queries";

// Trailing-edge debounce for slider drags. Sliders fire onChange every
// few pixels — without debouncing we'd PATCH the API 50+ times during
// a single drag. 250ms keeps the last value as the one we ship, while
// still feeling near-instant when an admin lands on a target.
function useDebouncedTuningPatch(): (patch: PhysicsTuningBlob) => void {
  const mutation = useUpdatePhysicsTuning();
  const mutateRef = useRef(mutation.mutate);
  mutateRef.current = mutation.mutate;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<PhysicsTuningBlob>({});

  useEffect(() => {
    return () => {
      // Flush the pending patch on unmount so closing the dock mid-
      // drag doesn't drop the last value the admin landed on.
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (Object.keys(pendingRef.current).length > 0) {
        mutateRef.current(pendingRef.current);
        pendingRef.current = {};
      }
    };
  }, []);

  return useCallback((patch: PhysicsTuningBlob) => {
    // Merge top-level keys. For ghostBias, merge per-level so two
    // ghost-3 changes in flight collapse correctly without wiping
    // ghost-2.
    pendingRef.current = mergePatch(pendingRef.current, patch);
    if (timerRef.current != null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const payload = pendingRef.current;
      pendingRef.current = {};
      mutateRef.current(payload);
    }, 250);
  }, []);
}

// Context lets the Knob / GhostKnobs leaves push patches without
// changing every call site to thread the function explicitly.
const PushPatchContext = createContext<(p: PhysicsTuningBlob) => void>(() => {});

function mergePatch(
  base: PhysicsTuningBlob,
  patch: PhysicsTuningBlob,
): PhysicsTuningBlob {
  const out: PhysicsTuningBlob = { ...base, ...patch };
  const baseGhost = base.ghostBias as Record<string, Record<string, unknown>> | undefined;
  const patchGhost = patch.ghostBias as Record<string, Record<string, unknown>> | undefined;
  if (baseGhost || patchGhost) {
    const merged: Record<string, Record<string, unknown>> = { ...(baseGhost ?? {}) };
    if (patchGhost) {
      for (const [k, v] of Object.entries(patchGhost)) {
        merged[k] = { ...(merged[k] ?? {}), ...v };
      }
    }
    out.ghostBias = merged;
  }
  return out;
}

// ----- Public components -----

// The full set of knob sections. Caller wraps it however they want
// (dedicated page, drawer, modal). Includes a Reset button at the top.
export function PhysicsTunerControls() {
  const tuning = usePhysicsTuning();
  const pushPatch = useDebouncedTuningPatch();
  const resetMutation = useUpdatePhysicsTuning();
  const [gridOn, setGridOn] = useGridOverlay();
  return (
    <PushPatchContext.Provider value={pushPatch}>
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        {/* Admin-only screen-space grid overlay toggle. localStorage-
            persisted per device; rendered over the game viewport so
            we can speak in pixel coordinates while tuning (e.g.
            "the bag is at 250,600 but should be at 180,700"). */}
        <Button
          variant={gridOn ? "default" : "outline"}
          size="sm"
          onClick={() => setGridOn(!gridOn)}
          title="Toggle XY coordinate grid on the game viewport"
        >
          <Grid3x3 size={14} /> {gridOn ? "Grid on" : "Grid off"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (!confirm("Reset every value to its default?")) return;
            // Local reset for instant UI feedback. The server-side
            // reset is implemented as a wholesale write of the
            // defaults blob — the merge endpoint can't "remove" keys,
            // so we ship the entire default tree.
            resetTuning();
            resetMutation.mutate(DEFAULT_TUNING as unknown as PhysicsTuningBlob);
          }}
        >
          <RotateCcw size={14} /> Reset to defaults
        </Button>
      </div>
      <Section title="World" hint="Universal physics constants.">
        <Knob
          label="Gravity"
          path="gravity"
          tuning={tuning}
          min={-20}
          max={-1}
          step={0.1}
          unit="m/s²"
          help="Real Earth ≈ −9.81. More negative = bags drop faster, arcs flatter, harder to land soft. Less negative = floaty / moon-gravity."
        />
      </Section>
      <Section
        title="Slab sliding"
        hint="Bag behavior on contact + while sliding."
      >
        <Knob
          label="Friction coefficient"
          path="frictionMu"
          tuning={tuning}
          min={0}
          max={1}
          step={0.01}
          help="0 = ice (bags slide forever). 1 = velcro (bags stop instantly). Cornhole feel ≈ 0.3. Lower → more slide-to-hole 3s; higher → more on-board 1s."
        />
        <Knob
          label="Inelastic Y absorb"
          path="bagInelasticY"
          tuning={tuning}
          min={0}
          max={1}
          step={0.01}
          help="How much horizontal speed survives the landing thud. 0 = dead drop, no slide. 1 = perfectly bouncy, all energy kept. Lower → bags settle near where they hit."
        />
        <Knob
          label="Settle threshold"
          path="bagSettleThreshold"
          tuning={tuning}
          min={0.005}
          max={0.2}
          step={0.005}
          unit="m/s"
          help="Speed below which a sliding bag stops. Higher → bags settle sooner (less risk of sliding off the back). Lower → realistic creep into the hole."
        />
        <Knob
          label="Slab edge tolerance"
          path="bagEdgeTolerance"
          tuning={tuning}
          min={0}
          max={1}
          step={0.05}
          help="Multiplier on bag size for 'still on slab' detection. 0 = bag falls off the instant any corner overhangs. Higher → bags cling to the slab edge longer."
        />
        <Knob
          label="Hole lip capture"
          path="holeLipCapture"
          tuning={tuning}
          min={0}
          max={0.2}
          step={0.01}
          unit="m"
          help="How far past the hole rim a resting bag may hang and still tip in. 0 = bag must land fully inside the rim to score 3. Higher → bags 'headed for the hole' that stop a few cm short still drop in (reclaims near-miss 3s). ≈0.06 catches genuine lip-hangers without inflating the hole."
        />
      </Section>
      <Section
        title="Throw aim"
        hint="Where a perfect throw lands. Power gates DISTANCE — a weak swipe falls short."
      >
        <Knob
          label="Target Z fraction"
          path="targetZFraction"
          tuning={tuning}
          min={0}
          max={0.6}
          step={0.01}
          help="Where on the slab a FULL-POWER, dead-center swipe lands. 0 = front edge, 0.166 ≈ front-third sweet spot, 0.5 = center, 1 = back edge. Default 0.166 lets a perfect throw slide into the hole."
        />
        <Knob
          label="Power distance floor (weak-swipe reach)"
          path="powerDistanceFloor"
          tuning={tuning}
          min={0}
          max={1}
          step={0.01}
          help="Where the bag lands at ZERO power, as a fraction of the (start → target) distance. 0.4 = weak swipe falls ~40% of the way (well short of the board). Lower → power matters more. 1.0 = even no-power swipes reach the target (easy mode)."
        />
        <Knob
          label="Power distance ceiling (max-swipe reach)"
          path="powerDistanceCeiling"
          tuning={tuning}
          min={0.5}
          max={2.5}
          step={0.01}
          help="Where the bag lands at FULL power, as a fraction of the (start → target) distance. 1.0 = max-power lands exactly on target (no overshoot ever). 1.25 = 25% past target — a hard flick flies over the back of the board. Raise for more punishment on over-swipes."
        />
        <Knob
          label="Arc height ★ (lower = flatter)"
          path="flightTimeBase"
          tuning={tuning}
          min={0.6}
          max={2.5}
          step={0.05}
          unit="s"
          help="THE primary trajectory-height control. It's the throw's air-time, and the apex grows with the SQUARE of it — so this dominates how high the bag lofts. Lower = flatter & faster, higher = taller & slower. 1.3s natural; ~0.9 for a flat line-drive; over 1.8s the bag hangs/lofts out of frame. Adjust THIS to fix over-lofting (not 'Swipe-up loft' below)."
        />
        <Knob
          label="Power flattening (hard throws)"
          path="flightTimeFactor"
          tuning={tuning}
          min={0}
          max={1}
          step={0.05}
          help="How much a HARD throw flattens vs a soft one. Subtracted from Arc height per unit power: 0.35 = full-power throws are 0.35s shorter/flatter. Higher → power dramatically flattens the arc."
        />
        <Knob
          label="Apex clearance over target"
          path="vyLookahead"
          tuning={tuning}
          min={0}
          max={1.5}
          step={0.05}
          unit="m"
          help="Extra apex height aimed above the target. Higher → bags arc OVER the front lip (cleaner drop into the hole). 0 = laser-flat throws that can ricochet off the front edge. Secondary to Arc height for overall loft."
        />
        <Knob
          label="Swipe-up loft (minor / secondary)"
          path="vyDirYFactor"
          tuning={tuning}
          min={0}
          max={4}
          step={0.05}
          help="Small extra loft from a STEEP upward flick — scales only the swipe-angle component, which is tiny next to the arc itself, so this barely moves total height. For over/under-lofting use 'Arc height ★' above. Default 1.6."
        />
        <Knob
          label="Lateral sensitivity (off-axis)"
          path="vxFactor"
          tuning={tuning}
          min={0}
          max={12}
          step={0.1}
          help="How aggressively a left/right swipe component pushes the bag sideways. 0 = robot-perfect aim regardless of angle (boring). 6+ = pixel-precise swipe required. Default 5.5 punishes sloppy swipes hard."
        />
        <Knob
          label="Curve (dirX → vz)"
          path="vzDirXFactor"
          tuning={tuning}
          min={-2}
          max={2}
          step={0.05}
          help="Lateral aim slightly shifts forward target too — adds a curve hook. Positive = right-swipe lands long, left short. Keep small (~0.4) or throws feel uncalibrated."
        />
      </Section>
      <Section
        title="Power range"
        hint="Maps the swipe duration (ms) to a power value 0..1. Power then gates DISTANCE via the floor above."
      >
        <Knob
          label="Min swipe time (= max power)"
          path="powerMinMs"
          tuning={tuning}
          min={0}
          max={500}
          step={10}
          unit="ms"
          help="Anything FASTER than this clamps to max power. 150ms = a sharp flick. Raise to make max-power harder to reach."
        />
        <Knob
          label="Max swipe time (= min power)"
          path="powerMaxMs"
          tuning={tuning}
          min={400}
          max={3000}
          step={10}
          unit="ms"
          help="Anything SLOWER than this clamps to zero power. 1100ms = languid drag. Lower this number to make weak swipes hit the power floor sooner."
        />
      </Section>
      <Section
        title="Finger throw"
        hint="Bag pops at the bottom of the screen. User touches + flicks it directly with their finger (magnetic — bag tracks the touch until release)."
      >
        <Knob
          label="Bag X offset"
          path="fingerBagOffsetX"
          tuning={tuning}
          min={-1}
          max={1}
          step={0.01}
          unit="m"
          help="Idle horizontal position in camera-local space. 0 = centered; positive = right of center."
        />
        <Knob
          label="Bag Y offset"
          path="fingerBagOffsetY"
          tuning={tuning}
          min={-1.5}
          max={0.5}
          step={0.01}
          unit="m"
          help="Idle vertical position. Negative = lower in the frame. Default −0.5 sits the bag in the bottom third."
        />
        <Knob
          label="Bag depth (Z)"
          path="fingerBagOffsetZ"
          tuning={tuning}
          min={-2}
          max={-0.3}
          step={0.01}
          unit="m"
          help="How far in front of the camera. More negative = farther → smaller on screen but harder to overlap with the finger."
        />
        <Knob
          label="Bag size"
          path="fingerBagScale"
          tuning={tuning}
          min={0.5}
          max={3}
          step={0.05}
          help="Uniform size multiplier. 1.0 = real-world bag dimensions; 1.3 (default) makes it noticeably larger so phone taps land reliably. The hit-test radius scales with this."
        />
        <Knob
          label="Bag pitch (X rot)"
          path="fingerBagRotX"
          tuning={tuning}
          min={-90}
          max={90}
          step={1}
          unit="°"
          help="Tilt the bag's top face toward / away from camera. −45° (default) shows the top with the image clearly. 0 = bag stands vertical edge-on. Negative tilts top toward you."
        />
        <Knob
          label="Bag yaw (Y rot)"
          path="fingerBagRotY"
          tuning={tuning}
          min={-180}
          max={180}
          step={1}
          unit="°"
          help="Spin the bag horizontally. Use to align an off-axis bag photo with the mesh's UV orientation."
        />
        <Knob
          label="Bag roll (Z rot)"
          path="fingerBagRotZ"
          tuning={tuning}
          min={-180}
          max={180}
          step={1}
          unit="°"
          help="Twist the bag in the screen plane (clockwise / counter-clockwise from the camera's POV)."
        />
        <Knob
          label="In-flight / slide spin"
          path="bagYawSpinRate"
          tuning={tuning}
          min={-3}
          max={3}
          step={0.05}
          unit=" rad/s"
          help="Yaw spin rate for the thrown bag (in-flight AND while sliding on the board). Negative = clockwise from above (the natural direction of a sidearm flick). −0.55 (default) is a slow, gentle drift; bump to −1.5 for a noticeably spinning throw. Each throw gets ±30% jitter automatically."
        />
        <p className="text-xs text-muted-foreground mt-2 leading-snug">
          Bag image alignment (offset / zoom / rotation) is now baked
          into each bag asset — set it once in /admin/design's Align
          tool and every render path (finger bag, pile, in-flight,
          opponent magic-launch) uses the same values.
        </p>
      </Section>
      <Section
        title="World bag size"
        hint="ONE size for every bag in the world — finger bag, pile, in-flight, and the opponent/ghost bag all read from these. (The old separate 'World bag scale' knob is gone; it let the opponent bag drift to a different size than the in-hand bag.)"
      >
        <Knob
          label="Bag size"
          path="bagSize"
          tuning={tuning}
          min={0.05}
          max={0.2}
          step={0.005}
          unit="m"
          help="Edge length of the square bag mesh, meters. Single global source of truth — every bag in the world (yours, opponent's, ghost's) renders at this size. Real bags are 6×6 inches ≈ 0.15m; the renderer uses 0.095 by default because the camera FOV makes a true-size bag read tiny."
        />
        <Knob
          label="Bag thickness"
          path="bagThickness"
          tuning={tuning}
          min={0.005}
          max={0.04}
          step={0.0025}
          unit="m"
          help="How thick the bag sits between top and bottom faces, meters. Drives pile spacing and how high a bag sits on the slab when landed. Real bags are ~12mm; default 0.0125."
        />
        <Knob
          label="Landed pitch offset"
          path="bagLandedPitchOffsetDeg"
          tuning={tuning}
          min={-15}
          max={15}
          step={0.5}
          unit="°"
          help="Fine-tunes how flush the bag sits on the slab. Renderer auto-pitches to match the board's rake (~10° down toward player); this knob nudges that base ±a few degrees if the bag still reads slightly under or over the slab. 0 (default) = exact rake match."
        />
      </Section>
      <Section title="Camera & lighting" hint="Scene framing + light levels.">
        <Knob
          label="Camera FOV"
          path="cameraFov"
          tuning={tuning}
          min={30}
          max={80}
          step={1}
          unit="°"
          help="Field of view in degrees. Lower numbers compress perspective (telephoto / zoom-in) so the far board reads bigger relative to the player's hand — use this to bring the far board 'closer' without moving the camera. 55° (default) is wide-cinematic; 40-45° is the cornhole sweet spot; 30° goes near-orthographic."
        />
        <Knob
          label="Camera lookAt Y"
          path="cameraLookAtY"
          tuning={tuning}
          min={-1}
          max={2.5}
          step={0.05}
          unit="m"
          help="Where the camera focuses vertically. Lower (0.3) = horizon pushed up, boards take more screen. Higher (1.5+) = horizon centered, more sky visible."
        />
        <Knob
          label="Ambient intensity"
          path="ambientIntensity"
          tuning={tuning}
          min={0}
          max={1}
          step={0.01}
          help="Baseline light hitting everything. 0 = pitch dark shadows. 1 = flat / no shadows. Default 0.15 keeps shadows readable."
        />
        <Knob
          label="Hemisphere intensity"
          path="hemiIntensity"
          tuning={tuning}
          min={0}
          max={2}
          step={0.05}
          help="Sky-color light from above + ground-color bounce from below. Cheap 'outdoors' fake. Higher = brighter scene."
        />
        <Knob
          label="Sun intensity"
          path="sunIntensity"
          tuning={tuning}
          min={0}
          max={2.5}
          step={0.05}
          help="Directional sun light. Drives all the hard shadows. Higher = harsh midday; lower = overcast."
        />
        <Knob
          label="Sun position X"
          path="sunPositionX"
          tuning={tuning}
          min={-15}
          max={15}
          step={0.25}
          unit="m"
          help="Sun's horizontal position. Negative = left, positive = right. The light shines from this point toward the world origin (0,0,0), so positive X casts shadows toward -X (the left side of the scene). Shadows update live."
        />
        <Knob
          label="Sun position Y"
          path="sunPositionY"
          tuning={tuning}
          min={1}
          max={25}
          step={0.25}
          unit="m"
          help="Sun's height above the field. Higher = more overhead = shorter, harder shadows directly under objects. Lower = longer, more dramatic shadows raked across the ground (golden-hour effect)."
        />
        <Knob
          label="Sun position Z"
          path="sunPositionZ"
          tuning={tuning}
          min={-15}
          max={15}
          step={0.25}
          unit="m"
          help="Sun's depth position. Negative = sun is in front of the player (looking down the lane), shadows fall back toward camera. Positive = sun is behind the player, shadows fall toward the far board."
        />
        <Knob
          label="Ground tile count"
          path="groundTileCount"
          tuning={tuning}
          min={1}
          max={40}
          step={1}
          help="How many times the ground texture repeats across the 60m field. 20 (default) tiles tight for a close-up grass texture. Drop to 1 to lay a single panoramic image as a one-shot backdrop (good for full yard photos with grass + fence). Applied to custom scene images when set."
        />
      </Section>
      <Section title="Ghost AI" hint="Per-difficulty accuracy + power bias for AI opponents.">
        {Object.keys(tuning.ghostBias).map((level) => (
          <GhostKnobs key={level} level={level} bias={tuning.ghostBias[level]!} />
        ))}
      </Section>
    </div>
    </PushPatchContext.Provider>
  );
}

// Slide-in right-edge dock for live tuning during a virtual game.
// The caller is responsible for gating visibility on platform-admin
// status; this component just renders the affordance + drawer when
// asked to. Persists open/closed state in localStorage so an admin
// keeps the panel open across reloads.
export function PhysicsDock() {
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("physics.dock.open") === "true";
  });
  function toggle(next: boolean) {
    setOpen(next);
    try {
      window.localStorage.setItem("physics.dock.open", String(next));
    } catch {
      /* ignore */
    }
  }
  return (
    <>
      {/* Edge tab — sits on the right side of the viewport at vertical
          midline. Pulses subtly until first opened. */}
      {!open && (
        <button
          type="button"
          onClick={() => toggle(true)}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-40 inline-flex items-center gap-1.5 px-2 py-3 rounded-l-md bg-card/90 backdrop-blur border border-r-0 border-border text-foreground hover:bg-card text-xs font-display uppercase tracking-widest shadow"
          title="Open physics tuner"
        >
          <Sliders size={14} className="text-primary" />
          Tune
        </button>
      )}

      {/* The dock itself. Slides in from the right. On mobile it takes
          the full screen so the sliders aren't cramped. */}
      <aside
        className={`fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[26rem] bg-card border-l border-border shadow-2xl flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "translate-x-full pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
          <div className="inline-flex items-center gap-2">
            <Sliders size={16} className="text-primary" />
            <h2 className="text-sm font-display uppercase tracking-widest">
              Physics tuner
            </h2>
          </div>
          <button
            type="button"
            onClick={() => toggle(false)}
            className="text-muted-foreground hover:text-foreground p-1 inline-flex items-center gap-1"
            aria-label="Close physics tuner"
            title="Close"
          >
            <ChevronRight size={18} />
            <X size={14} className="sm:hidden" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          <PhysicsTunerControls />
        </div>
      </aside>
    </>
  );
}

// ----- Internals -----

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {hint && <CardDescription>{hint}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

type ScalarPath = Exclude<keyof PhysicsTuning, "ghostBias">;

function Knob({
  label,
  path,
  tuning,
  min,
  max,
  step,
  unit,
  help,
}: {
  label: string;
  path: ScalarPath;
  tuning: PhysicsTuning;
  min: number;
  max: number;
  step: number;
  unit?: string;
  help?: string;
}) {
  const pushPatch = useContext(PushPatchContext);
  const value = tuning[path] as number;
  const defaultValue = DEFAULT_TUNING[path] as number;
  const dirty = value !== defaultValue;
  return (
    <KnobRow
      label={label}
      value={value}
      dirty={dirty}
      min={min}
      max={max}
      step={step}
      unit={unit}
      help={help}
      onChange={(v) => {
        // Local first for instant 60fps feedback in the renderer; server
        // PATCH is debounced trailing-edge inside pushPatch.
        setTuning({ [path]: v } as Partial<PhysicsTuning>);
        pushPatch({ [path]: v });
      }}
    />
  );
}

function GhostKnobs({ level, bias }: { level: string; bias: GhostBias }) {
  const pushPatch = useContext(PushPatchContext);
  const def = DEFAULT_TUNING.ghostBias[level]!;
  const pushGhost = (patch: Partial<GhostBias>) =>
    pushPatch({ ghostBias: { [level]: patch } });
  return (
    <div className="rounded-md border border-border p-3 space-y-2">
      <p className="text-xs font-display uppercase tracking-widest">{level}</p>
      <KnobRow
        label="Aim sigma"
        value={bias.aimSigma}
        dirty={bias.aimSigma !== def.aimSigma}
        min={0}
        max={1}
        step={0.01}
        onChange={(v) => {
          setGhostBias(level, { aimSigma: v });
          pushGhost({ aimSigma: v });
        }}
      />
      <KnobRow
        label="Power mean"
        value={bias.powerMs}
        dirty={bias.powerMs !== def.powerMs}
        min={500}
        max={2000}
        step={10}
        unit="ms"
        onChange={(v) => {
          setGhostBias(level, { powerMs: v });
          pushGhost({ powerMs: v });
        }}
      />
      <KnobRow
        label="Power jitter"
        value={bias.powerJitter}
        dirty={bias.powerJitter !== def.powerJitter}
        min={0}
        max={600}
        step={10}
        unit="ms"
        onChange={(v) => {
          setGhostBias(level, { powerJitter: v });
          pushGhost({ powerJitter: v });
        }}
      />
      <KnobRow
        label="Loft"
        value={bias.loft}
        dirty={bias.loft !== def.loft}
        min={0}
        max={1}
        step={0.01}
        onChange={(v) => {
          setGhostBias(level, { loft: v });
          pushGhost({ loft: v });
        }}
      />
    </div>
  );
}

function KnobRow({
  label,
  value,
  dirty,
  min,
  max,
  step,
  unit,
  help,
  onChange,
}: {
  label: string;
  value: number;
  dirty: boolean;
  min: number;
  max: number;
  step: number;
  unit?: string;
  help?: string;
  onChange: (next: number) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[1fr_auto] items-baseline gap-3">
        <label className="text-sm font-medium inline-flex items-center gap-2">
          {label}
          {dirty && (
            <span
              className="text-[9px] font-display uppercase tracking-widest text-primary"
              title="modified from default"
            >
              ◆
            </span>
          )}
        </label>
        <span className="text-xs text-muted-foreground tabular-nums">
          {value.toFixed(step < 0.1 ? 3 : step < 1 ? 2 : 0)}
          {unit ? ` ${unit}` : ""}
        </span>
      </div>
      <div className="grid grid-cols-[1fr_5rem] gap-2 items-center">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full"
        />
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (Number.isFinite(n)) onChange(n);
          }}
          className="w-full px-1.5 py-0.5 text-xs rounded border border-input bg-transparent tabular-nums"
        />
      </div>
      {help && <p className="text-[11px] text-muted-foreground">{help}</p>}
    </div>
  );
}
